import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { z } from "zod";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import type { RequestCodeFn } from "./types";

export interface UseResend {
    resendBlocked: boolean;
    handleResend: () => Promise<void>;
}

const RESEND_DELAY = 60_000;

export function useResend(request: RequestCodeFn): UseResend {
    const [resendBlocked, setResendBlocked] = useState<boolean>(false);
    const { error: errorToast } = useToaster();

    useEffect(() => {
        let timer: number;
        if (resendBlocked) {
            timer = window.setTimeout(() => {
                setResendBlocked(false);
            }, RESEND_DELAY);
        }

        return () => {
            window.clearTimeout(timer);
        };
    }, [resendBlocked]);

    const handleResend = useCallback(async () => {
        try {
            setResendBlocked(true);
            await request();
        } catch (error) {
            const { success, data } = z
                .object({ message: z.string().optional() })
                .safeParse(
                    axios.isAxiosError(error)
                        ? error.response?.data
                        : undefined,
                );

            errorToast(
                success && data.message
                    ? data.message
                    : "Failed to send the code",
            );

            setResendBlocked(false);
        }
    }, [errorToast, request]);

    return {
        handleResend,
        resendBlocked,
    };
}
